import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Container, Form, Button, Alert } from 'react-bootstrap';
import { FaFacebook } from 'react-icons/fa';
import { useAlert } from "../AlertContext"; 
import GoogleLoginButton from '../components/GoogleLoginButton';
import './pages.css';

const AuthPage = () => {
  const { login, signup } = useAuth();
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    identifier: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const { showAlert } = useAlert();
  const [adminLoginAttempt, setAdminLoginAttempt] = useState(false);

  useEffect(() => {
    const checkProfileCompletion = () => {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        
        // Check if user is admin and redirect to admin dashboard
        if (user.role === 'admin') {
          navigate('/admin-login', { 
            replace: true 
          });
          return;
        }
        
        if (user.needsProfileCompletion) {
          navigate('/complete-profile', { 
            state: { fromGoogleSignup: true } 
          });
        }
      }
    };

    checkProfileCompletion();
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    const errors = {};

    if (isLogin) {
      if (!formData.identifier) {
        errors.identifier = 'Email or phone is required';
      }
      if (!formData.password) {
        errors.password = 'Password is required';
      }
    } else {
      if (!formData.name) {
        errors.name = 'Name is required';
      }
      if (!formData.phone) {
        errors.phone = 'Phone number is required';
      }
      if (!formData.email) {
        errors.email = 'Email is required';
      }
      if (!formData.password) {
        errors.password = 'Password is required';
      }
    }

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setAdminLoginAttempt(false);
    
    try {
      if (isLogin) {
        const userData = await login(formData.identifier, formData.password);
        
        // Check if the logged-in user is an admin
        if (userData.role === 'admin') {
          setAdminLoginAttempt(true);
          showAlert("Admin users must login through the admin login page", "danger");
          
          // Logout the admin user immediately
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          
          // Redirect to admin login after a short delay
          setTimeout(() => {
            navigate('/admin-login');
          }, 3000);
          
          return;
        }
        
        showAlert("Login successful!", "success");
      } else {
        await signup({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          password: formData.password
        });
        showAlert("Signup successful!", "success");
      }

      // Get the current user data
      const userData = JSON.parse(localStorage.getItem('user'));
      
      // Redirect based on user role
      setTimeout(() => {
        if (userData && userData.role === 'admin') {
          navigate('/admin-login');
        } else {
          navigate('/dashboard');
        }
      }, 3000);
    } catch (error) {
      showAlert(error.message, "danger");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='auth-page'>
      <Container className="auth-template-container">
        <div className={`auth-forms-wrapper ${isLogin ? 'login-active' : 'signup-active'}`}>
          {/* Login Form */}
          <div className="card auth-form login-form">
            <div className="auth-header">
              <h2>Welcome Back!</h2>
            </div>

            <div className="form-container">
              <h5 className="mb-4 text-center">To keep connected with us please login with your personal info</h5>

              {adminLoginAttempt && (
                <Alert variant="danger" className="text-center">
                  <strong>Admin Access Required</strong>
                  <br />
                  Admin users must login through the admin login page. Redirecting...
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Email or Phone</Form.Label>
                  <Form.Control
                    type="text"
                    name="identifier"
                    placeholder="Enter your email or phone"
                    value={formData.identifier}
                    onChange={handleChange}
                    required
                  />
                  {errors.identifier && (
                    <Form.Text className="text-danger">
                      {errors.identifier}
                    </Form.Text>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                  />
                  {errors.password && (
                    <Form.Text className="text-danger">
                      {errors.password}
                    </Form.Text>
                  )}
                </Form.Group>

                <div className="forgot-password">
                  <Button variant="link" onClick={() => navigate("/forgot-password")}>Forgot Password?</Button>
                </div>

                <Button variant="primary" type="submit" className="auth-button" disabled={isLoading}>
                  {isLoading ? "Loading..." : isLogin ? "LOGIN" : "SIGN UP"}
                </Button>
              </Form>

              <div className="toggle-auth">
                <p>
                  Don't have an account?{' '}
                  <Button variant="link" onClick={() => setIsLogin(false)}>
                    Sign up
                  </Button>
                </p>
              </div>

              <div className="social-auth">
                <p>Or Login with</p>
                <div className="social-buttons">
                  <GoogleLoginButton isLogin={isLogin} />
                  <Button variant="outline-primary" className="facebook-btn">
                    <FaFacebook className="social-icon" /> Facebook
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Signup Form */}
          <div className="card auth-form signup-form">
            <div className="auth-header">
              <h2>Hello, Friend!</h2>
            </div>

            <div className="form-container">
              <h5 className="mb-4 text-center">Enter your personal details and start your delicious journey with us</h5>

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                  {errors.name && (
                    <Form.Text className="text-danger">
                      {errors.name}
                    </Form.Text>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    type="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                  {errors.phone && (
                    <Form.Text className="text-danger">
                      {errors.phone}
                    </Form.Text>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                  {errors.name && (
                    <Form.Text className="text-danger">
                      {errors.email}
                    </Form.Text>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                  />
                  {errors.name && (
                    <Form.Text className="text-danger">
                      {errors.password}
                    </Form.Text>
                  )}
                </Form.Group>

                <Button variant="primary" type="submit" className="auth-button" disabled={isLoading}>
                  {isLoading ? "Loading..." : isLogin ? "LOGIN" : "SIGN UP"}
                </Button>
              </Form>

              <div className="toggle-auth">
                <p>
                  Already have an account?{' '}
                  <Button variant="link" onClick={() => setIsLogin(true)}>
                    Login
                  </Button>
                </p>
              </div>

              <div className="social-auth">
                <p>Or Sign up with</p>
                <div className="social-buttons">
                  <GoogleLoginButton isLogin={isLogin} />
                  <Button variant="outline-primary" className="facebook-btn">
                    <FaFacebook className="social-icon" /> Facebook
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default AuthPage;